import { type Dispute } from "../../lib/api";

export const errorText = (failure: unknown) => failure instanceof Error ? failure.message : "Could not update the case. Please try again.";

export const ACTIVE_CASE_STATUSES = ["open", "unresolved", "referred"];

export function settlementOutcome(dispute: Dispute, resolution = dispute.resolution) {
  if (!resolution) return "The settlement is recorded.";
  if (resolution === "mutual_end") return "The agreement is ended by mutual agreement. Unfinished milestones are cancelled.";
  if (dispute.dealType === "order") return resolution === "release" ? "The purchase is completed." : resolution === "refund" ? "The purchase is ended and remaining payment returned." : "The purchase is settled with a split payment.";
  return resolution === "release" ? "The contract is completed." : "The contract is ended and remaining payment returned.";
}

// [disputes.payment-roles](../../../../docs/product/disputes.md#disputes-payment-roles): payment roles come from the owning agreement, never who filed the case.
// Unknown legacy sources use neutral labels without changing settlement ownership.
export function disputePartyLabels(dispute: Dispute) {
  if (dispute.dealType === "order") return { payer: "Buyer", recipient: "Seller" };
  switch (dispute.contractSourceType) {
    case "project": return { payer: "Client", recipient: "Contractor" };
    case "service":
    case "rfq": return { payer: "Client", recipient: "Service provider" };
    case "hire": return { payer: "Client", recipient: "Provider" };
    case "rfq_items": return { payer: "Buyer", recipient: "Supplier" };
    case "business": return { payer: "Buyer", recipient: "Seller" };
    default: return { payer: "Paying party", recipient: "Receiving party" };
  }
}

export function disputePaymentPerspective(dispute: Dispute) {
  const knownRole = ["buyer", "seller", "provider"].includes(dispute.openedByRole);
  const payerIsOpener = dispute.openedByRole === "buyer";
  const partyViewer = dispute.viewerRole !== "admin" && knownRole;
  const payerViewer = partyViewer && ((dispute.viewerRole === "opener") === payerIsOpener);
  return {
    knownRole, payerIsOpener, partyViewer, payerViewer,
    recipientViewer: partyViewer && !payerViewer
  };
}

export function disputeOutcomeLabels(dispute: Dispute) {
  const { payer, recipient } = disputePartyLabels(dispute);
  const agreementNames: Record<string, string> = {
    project: "project", service: "service contract", hire: "hire contract",
    rfq: "service contract", rfq_items: "supply agreement", business: "business sale",
  };
  const agreement = dispute.dealType === "order" ? "purchase" : agreementNames[dispute.contractSourceType ?? ""] ?? "agreement";
  // [disputes.payment-roles](../../../../docs/product/disputes.md#disputes-payment-roles): either side can file. Derive perspective from the recorded payment
  // role, and keep Admin or unknown legacy roles neutral.
  const { partyViewer, payerViewer, recipientViewer } = disputePaymentPerspective(dispute);
  return {
    mutual_end: `${payerViewer ? "Pay" : recipientViewer ? "Receive payment" : "Settle"} and end the ${agreement}`,
    refund: payerViewer ? "Return payment to me" : `Return payment to ${payer.toLowerCase()}`,
    release: recipientViewer ? "Receive payment" : `Pay ${recipient.toLowerCase()}`,
    split: partyViewer ? `Split payment with ${payerViewer ? recipient.toLowerCase() : payer.toLowerCase()}`
      : `Split payment between ${payer.toLowerCase()} and ${recipient.toLowerCase()}`,
  };
}
